import type { ReactNode } from "react";
import { Button } from "./Button";
import { Callout } from "./Callout";
import { VisuallyHidden } from "./VisuallyHidden";
import type { ApiError } from "../api/client";
import type { ResourceState } from "../hooks/useApiResource";

/**
 * The loading, error and data states of one section, rendered the same way everywhere.
 *
 * Canon `docs/TESTING.md` treats these as separately failable behaviour, and a section that
 * invented its own spinner or its own error wording would be one more place for the three
 * to drift apart. Empty is **not** handled here: an empty board and an empty ranking mean
 * different things (canon § 6 makes a short board a normal cold-start state), so each
 * section writes its own empty copy.
 *
 * The error message is this repo's, not core's: core's messages are contract text, and some
 * of them describe conditions a visitor can do nothing about.
 */
export function AsyncSection<T>({
  state,
  reload,
  loadingLabel,
  skeleton,
  errorTitle,
  children,
}: {
  state: ResourceState<T>;
  reload: () => void;
  /** What is loading, for assistive technology: "Loading today's board". */
  loadingLabel: string;
  skeleton: ReactNode;
  errorTitle: string;
  children: (data: T) => ReactNode;
}) {
  if (state.status === "loading") {
    return (
      <div role="status" aria-busy="true">
        <VisuallyHidden>{loadingLabel}</VisuallyHidden>
        {skeleton}
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <Callout
        tone="error"
        title={errorTitle}
        actions={
          <Button variant="secondary" onClick={reload}>
            Try again
          </Button>
        }
      >
        {describe(state.error)}
      </Callout>
    );
  }

  return <>{children(state.data)}</>;
}

function describe(error: ApiError): string {
  if (error.code === "NETWORK_UNAVAILABLE") {
    return "Dealbid couldn't reach the deals service. Your connection may have dropped.";
  }
  return "The deals service didn't answer as expected. This is on Dealbid's side, not yours.";
}
